import React from "react";

class OptionChainErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isDataError: false,
      isConnectionError: false,
      lastWorkingData: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(
      "OptionChainErrorBoundary caught an error:",
      error,
      errorInfo,
    );

    // Categorize error types for specific handling
    const errorMessage = error.message.toLowerCase();
    const isDataError =
      errorMessage.includes("data") ||
      errorMessage.includes("fetch") ||
      errorMessage.includes("parse");
    const isConnectionError =
      errorMessage.includes("network") ||
      errorMessage.includes("connection") ||
      errorMessage.includes("timeout");

    this.setState({
      error: error,
      errorInfo: errorInfo,
      isDataError,
      isConnectionError,
    });

    // Preserve last working data if available
    if (this.props.lastWorkingData) {
      this.setState({ lastWorkingData: this.props.lastWorkingData });
    }

    // Log specific option chain errors
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: `OptionChain Error: ${error.toString()}`,
        fatal: false,
        custom_map: {
          error_type: isDataError
            ? "data_error"
            : isConnectionError
              ? "connection_error"
              : "component_error",
        },
      });
    }
  }

  handleDataRefresh = async () => {
    try {
      // Call parent's data refresh function if provided
      if (this.props.onDataRefresh) {
        await this.props.onDataRefresh();
      }

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isDataError: false,
        isConnectionError: false,
      });
    } catch (error) {
      console.error("Error during data refresh:", error);
    }
  };

  handleFallbackMode = () => {
    // Switch to fallback mode with last working data
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onFallbackMode) {
      this.props.onFallbackMode(this.state.lastWorkingData);
    }
  };

  render() {
    if (this.state.hasError) {
      const { selectedIndex, selectedExpiry } = this.props;

      return (
        <div className="option-chain-error-boundary">
          <div className="alert alert-warning" role="alert">
            <h5 className="alert-heading">
              <i className="fas fa-chart-line me-2"></i>
              Home Unavailable
            </h5>

            <p>
              The option chain for <strong>{selectedIndex}</strong>
              {selectedExpiry && ` (${selectedExpiry})`} encountered an error.
            </p>

            {this.state.isDataError && (
              <div className="mb-3">
                <h6 className="text-warning">
                  <i className="fas fa-database me-1"></i>
                  Data Error Detected
                </h6>
                <p className="mb-2">
                  Unable to fetch or process option chain data.
                </p>
                <button
                  className="btn btn-warning btn-sm me-2"
                  onClick={this.handleDataRefresh}
                >
                  <i className="fas fa-sync me-1"></i>
                  Refresh Data
                </button>
              </div>
            )}

            {this.state.isConnectionError && (
              <div className="mb-3">
                <h6 className="text-danger">
                  <i className="fas fa-wifi me-1"></i>
                  Connection Error
                </h6>
                <p className="mb-2">Lost connection to market data servers.</p>
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-refresh me-1"></i>
                  Reconnect
                </button>
              </div>
            )}

            {this.state.lastWorkingData && (
              <div className="mb-3">
                <h6 className="text-info">
                  <i className="fas fa-history me-1"></i>
                  Fallback Available
                </h6>
                <p className="mb-2">
                  Continue with last working data (may be outdated).
                </p>
                <button
                  className="btn btn-info btn-sm me-2"
                  onClick={this.handleFallbackMode}
                >
                  <i className="fas fa-play me-1"></i>
                  Use Cached Data
                </button>
              </div>
            )}

            <hr />
            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={() =>
                  this.setState({
                    hasError: false,
                    error: null,
                    errorInfo: null,
                  })
                }
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => (window.location.href = "/home")}
              >
                <i className="fas fa-home me-1"></i>
                Reset Home
              </button>

              {/* Quick access to other indexes */}
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  Switch Index
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <a
                      className="dropdown-item"
                      href="/home?index=NIFTY"
                    >
                      NIFTY
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="/home?index=SENSEX"
                    >
                      SENSEX
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="/home?index=BANKNIFTY"
                    >
                      BANKNIFTY
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="/home?index=FINNIFTY"
                    >
                      FINNIFTY
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Error details for debugging */}
            <details className="mt-3">
              <summary className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-bug me-1"></i>
                Error Details
              </summary>
              <div className="mt-2 p-2 bg-light border rounded">
                <small>
                  <strong>Error:</strong>{" "}
                  {this.state.error && this.state.error.message}
                  <br />
                  <strong>Index:</strong> {selectedIndex}
                  <br />
                  <strong>Expiry:</strong> {selectedExpiry}
                  <br />
                  <strong>Time:</strong>{" "}
                  {new Date().toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })}
                </small>
              </div>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for easy wrapping of option chain components
export const withOptionChainErrorBoundary = (WrappedComponent) => {
  return React.forwardRef((props, ref) => (
    <OptionChainErrorBoundary
      selectedIndex={props.selectedIndex}
      selectedExpiry={props.selectedExpiry}
      onDataRefresh={props.onDataRefresh}
      onFallbackMode={props.onFallbackMode}
      lastWorkingData={props.lastWorkingData}
    >
      <WrappedComponent {...props} ref={ref} />
    </OptionChainErrorBoundary>
  ));
};

export default OptionChainErrorBoundary;
